import { useTranslation } from 'react-i18next'

import { formatPlace } from '../../../common/place'
import { Value, BooleanValue } from '../../data'
import { mergeClassName } from '../../../utils/mergeClassName'
import { useGetLocationLabel, type Move } from '../../../modules/moves'
import { getLocationById } from '../../../modules/moves/core/locations.utils'

type AddressRowProps = {
  move: Move
  locationId: string
  wide?: boolean
}

const AddressRow: React.FC<AddressRowProps> = ({
  move,
  locationId,
  wide = false,
}) => {
  const { t } = useTranslation()

  const location = getLocationById(move.locations, locationId)
  const label = useGetLocationLabel()(move.locations, locationId)
  if (!location || !label) {
    return null
  }

  return (
    <div className="h-full flex-1">
      <h3 className="my-2 font-bold text-black">
        { label }
      </h3>

      <div className={mergeClassName(
        'rounded-md border p-4 shadow lg:border-none',
        wide && 'md:flex md:flex-row gap-4 md:items-center flex-1',
      )}
      >
        <Value className={mergeClassName(
          'whitespace-pre-wrap',
          wide && 'md:w-1/2',
        )}
        >
          { formatPlace(
            location.address,
            {
              addressOnly: true,
              compact: true,
            },
          ).join('\n') }
        </Value>

        <div className="flex-1 ">
          <div
            className={mergeClassName(
              'my-4',
              wide && 'md:my-2',
            )}
          >
            { (location?.nbStairs ?? 0) > 0
              ? (
                <>
                  <span className="font-body2 text-sm text-black">
                    { t('components.move.addresses.nbStairs') }
                  </span>
                  { ': ' }
                  { location.nbStairs }
                </>
                )
              : (
                <BooleanValue
                  value={false}
                  labelFalse={t('components.move.addresses.noStairs')}
                />
                ) }
          </div>

          { location.elevatorReservationRequired !== null && (
            <div
              className={mergeClassName(
                'my-4',
                wide && 'md:my-2',
              )}
            >
              <BooleanValue
                value={location.elevatorReservationRequired}
                labelTrue={t('components.move.addresses.elevatorNeeded')}
                labelFalse={t('components.move.addresses.elevatorNotNeeded')}
              />
            </div>
          ) }
        </div>
      </div>
    </div>
  )
}

export default AddressRow
